import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createOperationsTemplateAPI,
  getOperationsTemplatesAPI,
  getOperationsTemplateByIdAPI,
  updateOperationsTemplateAPI,
  deleteOperationsTemplateAPI,
  restoreOperationsTemplateAPI,
  getOperationsEntryAPI,
  saveOperationsEntryAPI,
  getDeletedOperationsTemplatesAPI,
  searchOperationsAPI,
} from "../../api/operationsTemplates";

export const searchOperations = createAsyncThunk(
  "operations/search",
  async (params, { rejectWithValue, signal }) => {
    try {
      return await searchOperationsAPI(params, signal);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createOperationsTemplate = createAsyncThunk(
  "operations/createTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      return await createOperationsTemplateAPI(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getOperationsTemplates = createAsyncThunk(
  "operations/getTemplates",
  async (hotelId, { rejectWithValue }) => {
    try {
      return await getOperationsTemplatesAPI(hotelId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getOperationsTemplateById = createAsyncThunk(
  "operations/getTemplateById",
  async (templateId, { rejectWithValue }) => {
    try {
      return await getOperationsTemplateByIdAPI(templateId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateOperationsTemplate = createAsyncThunk(
  "operations/updateTemplate",
  async ({ templateId, data }, { rejectWithValue }) => {
    try {
      return await updateOperationsTemplateAPI({
        templateId,
        data,
      });
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteOperationsTemplate = createAsyncThunk(
  "operations/deleteTemplate",
  async ({ templateId, lastKnownUpdatedAt }, { rejectWithValue }) => {
    try {
      return await deleteOperationsTemplateAPI({
        templateId,
        lastKnownUpdatedAt,
      });
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getDeletedOperationsTemplates = createAsyncThunk(
  "operations/getDeletedTemplates",
  async (payload, { rejectWithValue }) => {
    try {
      return await getDeletedOperationsTemplatesAPI(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const restoreOperationsTemplate = createAsyncThunk(
  "operations/restoreTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      return await restoreOperationsTemplateAPI(templateId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getOperationsEntry = createAsyncThunk(
  "operations/getEntry",
  async (payload, { rejectWithValue }) => {
    try {
      return await getOperationsEntryAPI(payload);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const saveOperationsEntry = createAsyncThunk(
  "operations/saveEntry",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await saveOperationsEntryAPI(payload);
      const query = getState().operations.entryQuery;
      if (query && ["hotelId", "templateId", "operationDate"].every((key) => query[key] === payload[key])) {
        await dispatch(getOperationsEntry(query));
      }
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const operationsSlice = createSlice({
  name: "operations",

  initialState: {
    searchSuggestions: [],
    searchLoading: false,
    searchError: null,
    searchRequestId: null,
    templates: [],
    selectedTemplate: null,

    deletedTemplates: [],

    operationsEntry: null,
    entryRequestId: null,
    entryQuery: null,

    createLoading: false,
    createSuccess: false,
    createError: null,

    fetchLoading: false,
    fetchError: null,

    fetchOneLoading: false,
    fetchOneError: null,

    updateLoading: false,
    updateSuccess: false,
    updateError: null,

    deleteLoading: false,
    deleteSuccess: false,
    deleteError: null,

    restoreLoading: false,
    restoreSuccess: false,
    restoreError: null,

    entryLoading: false,
    entryError: null,

    saveEntryLoading: false,
    saveEntrySuccess: false,
    saveEntryError: null,

    deletedTemplatesLoading: false,
    deletedTemplatesError: null,

    deletedTemplatesPagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  },

  reducers: {
    clearOperationsSearch: (state) => {
      state.searchSuggestions = [];
      state.searchLoading = false;
      state.searchError = null;
      state.searchRequestId = null;
    },
    resetCreateOperationsTemplateState: (state) => {
      state.createLoading = false;
      state.createSuccess = false;
      state.createError = null;
    },

    resetUpdateOperationsTemplateState: (state) => {
      state.updateLoading = false;
      state.updateSuccess = false;
      state.updateError = null;
    },

    resetDeleteOperationsTemplateState: (state) => {
      state.deleteLoading = false;
      state.deleteSuccess = false;
      state.deleteError = null;
    },

    resetRestoreOperationsTemplateState: (state) => {
      state.restoreLoading = false;
      state.restoreSuccess = false;
      state.restoreError = null;
    },

    resetSaveOperationsEntryState: (state) => {
      state.saveEntryLoading = false;
      state.saveEntrySuccess = false;
      state.saveEntryError = null;
    },

    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },

    clearOperationsEntry: (state) => {
      state.entryRequestId = null;
      state.entryQuery = null;
      state.operationsEntry = null;
      state.entryLoading = false;
      state.entryError = null;
    },

    clearDeletedOperationsTemplates: (state) => {
      state.deletedTemplates = [];
      state.deletedTemplatesPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      };
      state.deletedTemplatesLoading = false;
      state.deletedTemplatesError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(searchOperations.pending, (state, action) => {
        state.searchRequestId = action.meta.requestId;
        state.searchSuggestions = [];
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchOperations.fulfilled, (state, action) => {
        if (state.searchRequestId !== action.meta.requestId) return;
        state.searchLoading = false;
        state.searchSuggestions = action.payload?.data?.suggestions || [];
      })
      .addCase(searchOperations.rejected, (state, action) => {
        if (state.searchRequestId !== action.meta.requestId) return;
        state.searchLoading = false;
        state.searchError = action.meta.aborted ? null : action.payload || action.error.message;
      })

      /* ================================
         CREATE TEMPLATE
      ================================ */

      .addCase(createOperationsTemplate.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.createError = null;
      })
      .addCase(createOperationsTemplate.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;

        const template = action.payload?.data?.template || action.payload?.data || action.payload;

        state.templates.unshift(template);
      })
      .addCase(createOperationsTemplate.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })

      /* ================================
         GET TEMPLATES
      ================================ */

      .addCase(getOperationsTemplates.pending, (state) => {
        state.fetchLoading = true;
        state.fetchError = null;
      })
      .addCase(getOperationsTemplates.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = null;

        state.templates = action.payload?.data?.templates || action.payload?.data || [];
      })
      .addCase(getOperationsTemplates.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload;
      })

      /* ================================
         GET TEMPLATE BY ID
      ================================ */

      .addCase(getOperationsTemplateById.pending, (state) => {
        state.fetchOneLoading = true;
        state.fetchOneError = null;
      })
      .addCase(getOperationsTemplateById.fulfilled, (state, action) => {
        state.fetchOneLoading = false;

        state.selectedTemplate =
          action.payload?.data?.template || action.payload?.data || action.payload;
      })
      .addCase(getOperationsTemplateById.rejected, (state, action) => {
        state.fetchOneLoading = false;
        state.fetchOneError = action.payload;
      })

      /* ================================
         UPDATE TEMPLATE
      ================================ */

      .addCase(updateOperationsTemplate.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.updateError = null;
      })
      .addCase(updateOperationsTemplate.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;

        const updatedTemplate =
          action.payload?.data?.template || action.payload?.data || action.payload;

        state.templates = state.templates.map((item) =>
          item._id === updatedTemplate._id ? updatedTemplate : item
        );

        state.selectedTemplate = updatedTemplate;
      })
      .addCase(updateOperationsTemplate.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })

      /* ================================
         DELETE TEMPLATE
      ================================ */

      .addCase(deleteOperationsTemplate.pending, (state) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteError = null;
      })
      .addCase(deleteOperationsTemplate.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;

        const deletedId = action.meta.arg.templateId;

        state.templates = state.templates.filter((item) => item._id !== deletedId);
      })
      .addCase(deleteOperationsTemplate.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      })

      /* ================================
         GET DELETED TEMPLATES
         ================================ */

      .addCase(getDeletedOperationsTemplates.pending, (state) => {
        state.deletedTemplatesLoading = true;
        state.deletedTemplatesError = null;
      })
      .addCase(getDeletedOperationsTemplates.fulfilled, (state, action) => {
        state.deletedTemplatesLoading = false;
        state.deletedTemplatesError = null;

        const data = action.payload?.data || {};

        state.deletedTemplates = data.items || [];
        state.deletedTemplatesPagination = data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
        };
      })
      .addCase(getDeletedOperationsTemplates.rejected, (state, action) => {
        state.deletedTemplatesLoading = false;
        state.deletedTemplatesError = action.payload;
      })

      /* ================================
         RESTORE TEMPLATE
      ================================ */

      .addCase(restoreOperationsTemplate.pending, (state) => {
        state.restoreLoading = true;
        state.restoreSuccess = false;
        state.restoreError = null;
      })
      .addCase(restoreOperationsTemplate.fulfilled, (state) => {
        state.restoreLoading = false;
        state.restoreSuccess = true;
      })
      .addCase(restoreOperationsTemplate.rejected, (state, action) => {
        state.restoreLoading = false;
        state.restoreError = action.payload;
      })

      /* ================================
         LOAD ENTRY
      ================================ */

      .addCase(getOperationsEntry.pending, (state, action) => {
        state.entryRequestId = action.meta.requestId;
        state.entryQuery = action.meta.arg;
        state.operationsEntry = null;
        state.entryLoading = true;
        state.entryError = null;
      })
      .addCase(getOperationsEntry.fulfilled, (state, action) => {
        if (state.entryRequestId !== action.meta.requestId) return;
        state.entryLoading = false;

        state.operationsEntry = action.payload?.data || action.payload;
      })
      .addCase(getOperationsEntry.rejected, (state, action) => {
        if (state.entryRequestId !== action.meta.requestId) return;
        state.entryLoading = false;
        state.entryError = action.payload;
      })

      /* ================================
         SAVE ENTRY
      ================================ */

      .addCase(saveOperationsEntry.pending, (state) => {
        state.saveEntryLoading = true;
        state.saveEntrySuccess = false;
        state.saveEntryError = null;
      })
      .addCase(saveOperationsEntry.fulfilled, (state, action) => {
        state.saveEntryLoading = false;
        state.saveEntrySuccess = true;
      })
      .addCase(saveOperationsEntry.rejected, (state, action) => {
        state.saveEntryLoading = false;
        state.saveEntryError = action.payload;
      });
  },
});

export const {
  clearOperationsSearch,
  resetCreateOperationsTemplateState,
  resetUpdateOperationsTemplateState,
  resetDeleteOperationsTemplateState,
  resetRestoreOperationsTemplateState,
  resetSaveOperationsEntryState,
  clearSelectedTemplate,
  clearOperationsEntry,
  clearDeletedOperationsTemplates,
} = operationsSlice.actions;

export default operationsSlice.reducer;
